import { useEffect, useState } from "react";

type DeviceType = "Mobile" | "Tablet" | "Desktop" | "Unknow";

export interface BrowserInfo {
  browser: string;
  os: string;
  deviceType: DeviceType;
  isTouch: boolean;
  screen: {
    width: number;
    height: number;
  };
}

const defaultBrowserInfo: BrowserInfo = {
  browser: "Unknow",
  deviceType: "Unknow",
  os: "Unknow",
  isTouch: false,
  screen: {
    width: -1,
    height: -1
  }
}

function detectBrowser(userAgent: string): string {
  if (/edg/i.test(userAgent)) return "Edge Browser";
  if (/chrome|crios/i.test(userAgent)) return "Chrome Browser";
  if (/firefox|fxios/i.test(userAgent)) return "Firefox Browser";
  if (/safari/i.test(userAgent) && !/chrome|crios|edg/i.test(userAgent))
    return "Safari Browser";
  return "Unknown";
}

function detectOS(userAgent: string): string {
  if (/windows/i.test(userAgent)) return "Windows";
  if (/mac os/i.test(userAgent)) return "macOS";
  if (/android/i.test(userAgent)) return "Android";
  if (/iphone|ipad|ipod/i.test(userAgent)) return "iOS";
  if (/linux/i.test(userAgent)) return "Linux";
  return "Unknown";
}

function detectDeviceType(width: number): DeviceType {
  if (width < 768) return "Mobile";
  if (width < 1024) return "Tablet";
  return "Desktop";
}

/**
 * Returns the Chrome/Chromium major version from the browser, or null if not Chrome-based.
 */
export function getChromeMajorVersion(): number | null {
  if (typeof navigator === "undefined") return null;

  const ua = navigator.userAgent;
  // Chrome and Chromium-based browsers include "Chrome/MAJOR.MINOR..."
  const match = ua.match(/Chrome\/(\d+)/);
  if (!match) return null;

  const major = parseInt(match[1], 10);
  return Number.isNaN(major) ? null : major;
}

export function useBrowserInfo(): BrowserInfo {
  const [info, setInfo] = useState<BrowserInfo>(defaultBrowserInfo);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const { userAgent, userAgentData } = (navigator as any);

    const os =
      userAgentData?.platform ??
      detectOS(userAgent);

    const browser =
      userAgentData?.brands?.[0]?.brand ??
      detectBrowser(userAgent);

    const width = window.innerWidth;
    const height = window.innerHeight;

    setInfo({
      browser,
      os,
      deviceType: detectDeviceType(width),
      isTouch: navigator.maxTouchPoints > 0,
      screen: { width, height },
    });
  }, []);

  return info;
}
